import React from "react";
import { NodeID } from "@hex-insights/app-modules";
import { isAfter, makeObjectFromArrayMap } from "@hex-insights/core";
import {
	useRecordViewMutation,
	useRecordViewsMutation,
	UserViewRecordIDsQuery,
	UserViewRecordListForTargetQuery,
	useUserViewRecordCountQuery,
	useUserViewRecordIDsQuery,
	useUserViewRecordListForTargetQuery,
} from "./GraphQL";

// TODO performance optimization, this all causes a lot of API calls to be made

export function useIsViewed(id: NodeID) {
	const { data } = useUserViewRecordCountQuery({
		variables: { filters: { targetIDEQ: id, user: [{ isCurrentEQ: true }] } },
		fetchPolicy: "cache-and-network",
	});
	const isViewed = !data || data.userViewRecordConnection.totalCount > 0;
	return isViewed;
}

export function useInitialIsViewed(id: NodeID) {
	const { data } = useUserViewRecordCountQuery({
		variables: { filters: { targetIDEQ: id, user: [{ isCurrentEQ: true }] } },
		fetchPolicy: "cache-and-network",
	});
	const hasLoadedRef = React.useRef(!!data);
	const hasData = !!data;
	const hasViewRecords = (data?.userViewRecordConnection.totalCount ?? 0) > 0;

	const [initialIsViewed, setInitialIsViewed] = React.useState(!hasData || hasViewRecords);
	React.useEffect(() => {
		if (hasLoadedRef.current || !hasData) {
			return;
		}
		setInitialIsViewed(hasViewRecords);
		hasLoadedRef.current = true;
	}, [data, hasData, hasViewRecords]);
	return initialIsViewed;
}

export function useAreViewed(ids: NodeID[]) {
	const { data } = useUserViewRecordIDsQuery({
		variables: { filters: { targetIDIn: ids, user: [{ isCurrentEQ: true }] } },
		fetchPolicy: "cache-and-network",
		skip: ids.length === 0,
	});
	return React.useMemo(() => toViewMap(ids, data), [ids, data]);
}

export function useInitialAreViewed(ids: NodeID[]) {
	const { data } = useUserViewRecordIDsQuery({
		variables: { filters: { targetIDIn: ids, user: [{ isCurrentEQ: true }] } },
		fetchPolicy: "cache-and-network",
		skip: ids.length === 0,
	});
	const hasLoadedRef = React.useRef(!!data);

	const [initialViewMap, setInitialViewMap] = React.useState(() => toViewMap(ids, data));

	React.useEffect(() => {
		if (hasLoadedRef.current || !data) {
			return;
		}
		setInitialViewMap(toViewMap(ids, data));
		hasLoadedRef.current = true;
	}, [ids, data]);

	return initialViewMap;
}

function toViewMap(ids: NodeID[], data: UserViewRecordIDsQuery | undefined) {
	if (!data) {
		return makeObjectFromArrayMap(ids, (e) => [e, true]);
	}
	const viewMap: Record<NodeID, boolean> = makeObjectFromArrayMap(ids, (e) => [e, false]);
	for (let i = 0; i < data.userViewRecordConnection.edges.length; i++) {
		const node = data.userViewRecordConnection.edges[i].node;
		viewMap[node.targetID] = true;
	}
	return viewMap;
}

export function useViewRecord(id: NodeID | null | undefined) {
	const { loading, data } = useUserViewRecordListForTargetQuery({
		variables: { filters: { targetIDEQ: id, user: [{ isCurrentEQ: true }] } },
		fetchPolicy: "cache-and-network",
		skip: !id,
	});

	const viewRecord = data?.userViewRecordConnection.edges[0]?.node ?? null;
	return { loading, viewRecord };
}

export function useInitialViewRecord(id: NodeID | null | undefined) {
	const { loading, data } = useUserViewRecordListForTargetQuery({
		variables: { filters: { targetIDEQ: id, user: [{ isCurrentEQ: true }] } },
		fetchPolicy: "cache-and-network",
		skip: !id,
	});
	const hasLoadedRef = React.useRef(!!data);

	const [initialViewRecord, setInitialViewRecord] = React.useState(
		() => data?.userViewRecordConnection.edges[0]?.node ?? null,
	);

	React.useEffect(() => {
		if (hasLoadedRef.current || !data) {
			return;
		}
		setInitialViewRecord(data.userViewRecordConnection.edges[0]?.node ?? null);
		hasLoadedRef.current = true;
	}, [data]);

	return { loading, initialViewRecord };
}

export function useViewRecords(ids: NodeID[]) {
	const { loading, data } = useUserViewRecordListForTargetQuery({
		variables: { filters: { targetIDIn: ids, user: [{ isCurrentEQ: true }] } },
		fetchPolicy: "cache-and-network",
		skip: ids.length === 0,
	});

	const viewRecordMap = React.useMemo(() => toViewRecordMap(ids, data), [ids, data]);

	return { loading, viewRecordMap };
}

export function useInitialViewRecords(ids: NodeID[]) {
	const { loading, data } = useUserViewRecordListForTargetQuery({
		variables: { filters: { targetIDIn: ids, user: [{ isCurrentEQ: true }] } },
		fetchPolicy: "cache-and-network",
		skip: ids.length === 0,
	});
	const hasLoadedRef = React.useRef(!!data);

	const [initialViewRecordMap, setInitialViewRecordMap] = React.useState(() => toViewRecordMap(ids, data));

	React.useEffect(() => {
		if (hasLoadedRef.current || !data) {
			return;
		}
		setInitialViewRecordMap(toViewRecordMap(ids, data));
		hasLoadedRef.current = true;
	}, [ids, data]);

	return { loading, initialViewRecordMap };
}

function toViewRecordMap(ids: NodeID[], data: UserViewRecordListForTargetQuery | undefined) {
	const viewRecordMap: Record<
		NodeID,
		UserViewRecordListForTargetQuery["userViewRecordConnection"]["edges"][0]["node"] | null
	> = makeObjectFromArrayMap(ids, (e) => [e, null]);
	if (!data) {
		return viewRecordMap;
	}
	for (let i = 0; i < data.userViewRecordConnection.edges.length; i++) {
		const node = data.userViewRecordConnection.edges[i].node;
		viewRecordMap[node.targetID] = node;
	}
	return viewRecordMap;
}

export function useRecordView(id: NodeID | null | undefined) {
	const [recordView, info] = useRecordViewMutation();
	const recordViewID = React.useCallback(() => {
		if (!id) {
			return;
		}

		const startTime = new Date();
		return recordView({
			variables: { id },
			update: (cache, result) => {
				const record = result.data?.recordView;
				if (!record) {
					return;
				}
				const wasCreated = isAfter(record.firstViewedAt, startTime);
				if (wasCreated) {
					cache.evict({ id: "ROOT_QUERY", fieldName: "userViewRecordConnection" });
				}
			},
		});
	}, [recordView, id]);
	return [recordViewID, info] as const;
}

export function useRecordViewOnMount(id: NodeID | null | undefined) {
	const [recordView, info] = useRecordView(id);
	React.useEffect(() => {
		recordView();
	}, [recordView]);
	return info;
}

export function useRecordViews(ids: NodeID[]) {
	const [recordViews, info] = useRecordViewsMutation();
	const recordViewIDs = React.useCallback(() => {
		const startTime = new Date();
		return recordViews({
			variables: { ids },
			update: (cache, result) => {
				const recordEdges = result.data?.recordViews.edges;
				if (!recordEdges) {
					return;
				}
				const anyCreated = recordEdges.some(({ node }) => isAfter(node.firstViewedAt, startTime));
				if (anyCreated) {
					cache.evict({ id: "ROOT_QUERY", fieldName: "userViewRecordConnection" });
				}
			},
		});
	}, [recordViews, ids]);
	return [recordViewIDs, info] as const;
}

export function useRecordViewsOnMount(ids: NodeID[]) {
	const [recordViews, info] = useRecordViews(ids);
	React.useEffect(() => {
		recordViews();
	}, [recordViews]);
	return info;
}
